import React, { useState, useEffect } from "react";
import PropTypes, { number } from "prop-types";
import moment from "moment";
import { Modal, LinearProgress, Button } from "@material-ui/core";
import ForwardIcon from "@material-ui/icons/Forward";
import styles from "./DayWiseFinishedGoodsLineTransferDisplay.module.css";
import {
  AttributesVirtualizedTable,
  CommentFormModal,
} from "..";
import TodayIcon from "@material-ui/icons/Today";
import { getDatesForRange, } from "../../utils/utils";
import {
  AGGREGATE_STATUS,
  API_RESOURCE_URLS,
  ASN_SELECTOR_HEADER_TEXT,
  ASN_STATUS,
  DATE_FORMAT,
  DISPLAY_MESSAGES,
  MESSAGE_TYPE,
} from "../../constants";
import { usePopupManager } from "../../providers/PopupManager/PopupManager";
import { API } from "../../apis/api";
import { useAuthorizationContext } from "../AuthorizationProvider/AuthorizationProvider";
import {
  buildErrorMessage,
  getLastAsnGeneratedDateForPlantAndLine,
} from "../../apis/calls";
import { formatDataToIncludeColour } from "../../utils/tableUtils";
import { agencies, reasons, statusStringMap } from "../../staticData";
import HeaderSearchBar from "../HeaderSearchBar.js/HeaderSearchBar";
import LineTransferSelectorDisplay from "../LineTransferSelectorDisplay/LineTransferSelectorDisplay";

const DayWiseFinishedGoodsLineTransferDisplay = ({
  open,
  handleClose,
  updateRows,
  startDate,
  endDate,
  plant,
  line,
  lineName,
  toline,
  type,
}) => {
  const { user } = useAuthorizationContext();
  const { showPopup ,showLoading ,showSuccess ,closePopUp , showError} = usePopupManager();
  const [activeDate, setActiveDate] = useState("");
  const [selectedDates, setSelectedDates] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [asnToTransfer, setAsnToTransfer] = useState(null); 
  const [searchTextMap, setSearchTextMap] = useState({
    asn: '',
    vc: '',
  });
  const [filteredAsnData, setFilteredAsnData] = useState([]);
  const [asnData, setAsnData] = useState({
    loading: false,
    data: [],
  });
  const [transferedAsnData, setTransferedAsnData] = useState({
    loading: false,
    data: [],
  });
  const [isAsnSelectorOpen, setAsnSelectorOpen] = useState(false);
  const [lastAsnGeneratedDate, setLastAsnGeneratedDate] = useState(null);
  const [isLineTransferASNDisplayOpen, setIsLineTransferModalOpen] = useState(
    null
  );
  const closeLineTransferModel = () => setIsLineTransferModalOpen(false);

  const openLineTransferModel = () =>{
    setIsLineTransferModalOpen(true)
  }

  const handleAsnsForLineTransferSelection = () => {
    const rowsWithoutSerialNumber = selectedRows.map(({ serialNumber, ...rest }) => rest);
    setAsnToTransfer(rowsWithoutSerialNumber); // Set multiple ASNs to transfer
    setAsnSelectorOpen(true);
  };

  const closeAsnSelector = () => {
    setAsnSelectorOpen(false);
  };
  
  const handleRowSelect = (row) => {
    const isSelected = selectedRows.some((selectedRow) => selectedRow.id === row.id);
    const newSelectedRows = isSelected
      ? selectedRows.filter((selectedRow) => selectedRow.id !== row.id)
      : [...selectedRows, row];
    setSelectedRows(newSelectedRows);
  };


  const serialNumberColumn = {
    title: "#",
    field: "_serialNumber",
    width: 54,
    render: (_, idx) => idx,
    headerStyles: {
      paddingLeft: 7,
      paddingRight: 2,
    },
  };

  const checkboxColumn = {
    title: "select",
    field: "_select",
    width: 30,
    renderHeader: () => {
      return (
        <input
          style={{marginRight: 350}}
          type="checkbox"
          checked={selectedRows.length === asnData.data.length && asnData.data.length > 0}
        />
      );
    },
    render: (row) => (
      <input
        style={{ marginTop: 0, marginBottom: 0}}
        type="checkbox"
        onChange={() => handleRowSelect(row)}
        checked={selectedRows.some((selectedRow) => selectedRow.id === row.id)}
        data-testid={`select-row-${row.id}`}
        id={`select-row-${row.id}`}
        name={`select-row-${row.id}`}
      />
    ),
  };
  

  const fixedAsnSelectorColumns = [
    {
      width: 50,
      title: '',
      field: null,
      render: row => {
        if (asnToTransfer.some(item =>  (row.asnId && item.asnId === row.asnId) || 
        (row.agsnId && item.agsnId === row.agsnId)))
          return (
            <ForwardIcon
              data-testid={'asn-to-line-Transfer'}
              className={styles.Forwerd}
            />
          );
        return null;
      },
    },
    {
      width: 120,
      title: type === 'AGSN' ? 'AgSN' : 'ASN', // Adjust title based on type
      field: type === 'AGSN' ? 'agsn' : 'asn',
      style: {
        textAlign: 'left',
      },
    },
    {
      width: 120,
      title: type === 'AGSN' ? 'Part Number' : 'Vc', 
      field: type === 'AGSN' ? 'materialNumber' : 'vc',
      style: {
        textAlign: 'left',
      },
    },
    {
      width: 280,
      title: type === 'AGSN' ? 'Part Description' : "Description",
      field: type === 'AGSN' ? 'materialDescription' :"vcDescription",
      style: {
        textAlign: 'left',
      },
    },
    {
      width: 130,
      title: 'VC Status',
      field: 'status',
      style: {
        textAlign: "center",
      },
      render: (row) => {
        if (row.status === ASN_STATUS.PLANNED)
          return <span style={{ opacity: 0.5 }}>Pending</span>;
        if (row.edited) return "Pending";
        return statusStringMap[row.status];
      },
    },
  ];
  
  const handleSearchTextChangeCurry = field => e => {
    const value = e.target.value;
    setSearchTextMap(prev => ({ ...prev, [field]: value }));
  };
  
  const clearSearchTextCurry = field => () =>
    setSearchTextMap(prev => ({ ...prev, [field]: '' }));
  
  const columns = [
    serialNumberColumn,
    checkboxColumn,

    {
      title: type === 'AGSN' ? 'AgSN' : 'ASN', // Adjust title based on type
      field: type === 'AGSN' ? 'agsn' : 'asn', 
      width: 120,
      style: {
        textAlign: "left",
      },
      renderHeader: () => (
        <HeaderSearchBar
          value={searchTextMap[type === 'AGSN' ? 'agsn' : 'asn']}
          handleChange={handleSearchTextChangeCurry(type === 'AGSN' ? 'agsn' : 'asn')}
          title={type === 'AGSN' ? 'AgSN' : 'ASN'}
          placeholder={`${type} search`}
          clearData={clearSearchTextCurry(type === 'AGSN' ? 'agsn' : 'asn')}
        />
      ),
    },
    {
      title: type === 'AGSN' ? 'Part Number' : 'Vc', // Adjust title based on type
      field: type === 'AGSN' ? 'materialNumber' : 'vc',
      width: 140,
      style: {
        textAlign: "left",
      },
      renderHeader: () => (
        <HeaderSearchBar
          value={searchTextMap[type === 'AGSN' ? 'materialNumber' : 'vc']}
          handleChange={handleSearchTextChangeCurry(type === 'AGSN' ? 'materialNumber' :'vc' )}
          title={type === 'AGSN' ? 'Part Number' : 'Vc'}
          placeholder={`${type} search`}
          clearData={clearSearchTextCurry(type === 'AGSN' ? 'materialNumber' : 'vc')}
        />
      ),
    },
    {
      title: type === 'AGSN' ? 'Part Description' : "Description",
      field: type === 'AGSN' ? 'materialDescription' :"vcDescription",
      width: 280,
      style: {
        textAlign: "left",
      },
    },
    {
      width: 160,
      title: "Planned FDT",
      field: "plannedDropTimestamp",
      render: (row) =>
        row.plannedDropTimestamp
          ? moment(row.plannedDropTimestamp).format("hh:mm A DD/MM/YYYY")
          : null,
    },
  ];

  const getAsnsOfLineForDate = async (plantId, lineId, toLine, date) => {
    try {
      const apiUrl =
        type === "AGSN"
          ? API_RESOURCE_URLS.getTransferableAgsns(
              plantId,
              lineId,
              toLine,
              date
            )
          : API_RESOURCE_URLS.getTransferableAsns(
              plantId,
              lineId,
              toLine,
              date
            );

      const response = await API.get(apiUrl);
      if (type === "AGSN") {
        response.data.sort((a, b) => a.sequence - b.sequence);
      } else {
        response.data.sort((a, b) => a.serialNumber - b.serialNumber);
      }
      const data = formatDataToIncludeColour(response.data);
      setAsnData({ loading: false, data });
    } catch (error) {
      console.error("Error fetching or processing data:", error);
      showError({
        contextText: `Failed to Fetch Transferable Asns/Agsns for Selected Line`,
        error: error,
      });
    }
  };

  const transferAsnsToLine = async (
    transferedAsnData,
    reason,
    agency,
    comment
  ) => {
    try {
      const selectedAsns = transferedAsnData.data.filter(
        (asn) => asn.isToBeLinedTranfer
      );

      if (selectedAsns.length === 0) {
        console.error("No ASNs selected for transfer");
        return;
      }
      const selectedIndices = transferedAsnData.data
        .map((asn, index) => (asn.isToBeLinedTranfer ? index : -1)) // Map selected ASNs to their indices
        .filter((index) => index !== -1); // Filter out non-selected ASNs
      const materialsSelectedForLineTransfer = selectedIndices.map((index, seqIndex) => ({
        number: index + 1, // Position is index + 1 (since indices start from 0)
        id: transferedAsnData.data[index].asnId || transferedAsnData.data[index].agsnId,
      }));

      const productionDateString = startDate.format(DATE_FORMAT.ISO);

      showLoading();
      await API.patch(
        API_RESOURCE_URLS.updateSequenceAfterLineTransfer(plant, toline),
        {
          line: toline,
          productionDate: productionDateString,
          reason: reason,
          agency: agency,
          comment: comment,
          sequenceToBeUpdated:materialsSelectedForLineTransfer,
        }
      );
      showSuccess({
        contextText: DISPLAY_MESSAGES.ASN_LINE_TRANSFER,
        info: "",
      });
      closePopUp();
      setIsLineTransferModalOpen(false);
      handleClose();
    } catch (err) {
      console.error(buildErrorMessage(err));
      showError({
        contextText: `Failed to Line Transfer the selected Asn/Agsn`,
        error: err,
      });
    }
  };

  const processAsnData = (asns) => {
    if (asns.length === 0) return [];
    const rows = asns.map((asn) => ({ ...asn, id: asn.asn || asn.agsn}));
    rows.sort((a, b) => a.serialNumber - b.serialNumber);
    return rows;
  };

  const fetchAsnsForDate = (productionDateString, plant, toline) =>
    API.get(API_RESOURCE_URLS.VC_ASN, {
      params: {
        from: productionDateString,
        to: productionDateString,
        plant: plant,
        line: toline,
        status: [
          ASN_STATUS.PLANNED,
          ASN_STATUS.DROPPED,
          ASN_STATUS.READY_FOR_DROP,
          ASN_STATUS.ROLLED_OUT,
          ASN_STATUS.SFLT,
          ASN_STATUS.WTCL,
        ],
      },
    });

    const fetchAgsnsForDate = (productionDateString, plant, toline) =>
        API.get(API_RESOURCE_URLS.agsn(plant, toline), {
          params: {
            date: productionDateString,
            status: [
              AGGREGATE_STATUS.ASN_GENERATED,
              AGGREGATE_STATUS.READY_FOR_DROP,
              AGGREGATE_STATUS.DROPPED,
              AGGREGATE_STATUS.BOOKED,
              AGGREGATE_STATUS.DISPATCHED,
            ],
          },
        });

  const handleLineTransfer = async () => {
    try {
      setTransferedAsnData((prevState) => ({ ...prevState, loading: true }));

      const productionDateString = startDate.format(DATE_FORMAT.ISO);
      const response = type === 'AGSN'
      ? await fetchAgsnsForDate(productionDateString, plant, toline)
      : await fetchAsnsForDate(productionDateString, plant, toline);
      if (type === 'AGSN') {
      response.data.sort((a, b) => a.sequence - b.sequence);
    } else {
      response.data.sort((a, b) => a.serialNumber - b.serialNumber);
    }
      const processedData = processAsnData(response.data);
      const data = formatDataToIncludeColour(processedData);
      setTransferedAsnData({ loading: false, data });
    } catch (err) {
      console.error(buildErrorMessage(err));
      showPopup({ type: MESSAGE_TYPE.INTERNAL_ERROR, error: err });
      setTransferedAsnData((prevState) => ({ ...prevState, loading: false }));
    }
  };

  useEffect(() => {
    const filterByField = (field, searchText) => data =>
      !searchText ||
      String(data[field]).toLowerCase().includes(searchText.toLowerCase());
  
      let filteredData = asnData.data;
      if (type === 'AGSN') {
        filteredData = filteredData.filter(filterByField('agsn', searchTextMap['agsn']));
        filteredData = filteredData.filter(filterByField('materialNumber', searchTextMap['materialNumber']));
      } else {
        filteredData = filteredData.filter(filterByField('asn', searchTextMap['asn']));
        filteredData = filteredData.filter(filterByField('vc', searchTextMap['vc']));
      }
    setFilteredAsnData(filteredData);
  }, [searchTextMap, asnData.data, type]);

  useEffect(() => {
    const productionDateString = startDate.format(DATE_FORMAT.ISO);
    getAsnsOfLineForDate(plant, line, toline, productionDateString);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plant, line]);

  useEffect(() => {
    handleLineTransfer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plant, line]);

  useEffect(() => {
    if (open) {
      if (!endDate || !startDate || endDate.isBefore(startDate, "day")) {
        setSelectedDates([]);
        setActiveDate(null);
      } else {
        const dates = getDatesForRange(startDate, endDate);
        setSelectedDates(dates);
        setActiveDate(dates[0]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, endDate, startDate, line]);

  useEffect(() => {
    getLastAsnGeneratedDateForPlantAndLine(plant.value, toline)
      .then((lastAsnGeneratedDateData) => {
        const dates = getDatesForRange(moment(), lastAsnGeneratedDateData);
        setActiveDate(dates[0]);
        setLastAsnGeneratedDate(lastAsnGeneratedDateData);
      })

      .catch((err) => {
        console.error(buildErrorMessage(err));
        showPopup({ error: err, contextText: "Failed to get asn dates" });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plant.value, toline]);

  const renderTableView = () => {
    return (
      <>
        <div className={styles.header}>
          <div className={styles.title}>
            <span style={{ display: "block" }}>ASN</span>
            <span style={{ display: "block", fontWeight: "600" }}>
              Line Transfer
            </span>
          </div>

          <div className={styles.calicon}>
            <span>For</span>
            <TodayIcon />
          </div>
          <div className={styles.datesSelectorBar}>
            <span className={styles.monthName}>
              {activeDate && activeDate.format("MMMM")}
            </span>
            <div className={styles.hrScroll}>
              <div className={styles.datesRow}>
                {selectedDates.length ? (
                  selectedDates.map((date) => (
                    <button
                      className={
                        date === activeDate
                          ? styles.activeDateButton
                          : styles.datesSelectorButton
                      }
                      onClick={() => setActiveDate(date)}
                      key={date}
                    >
                      {date.format("DD")}
                    </button>
                  ))
                ) : (
                  <div className={styles.notAvailableMsg}>
                    No ASNs available
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className={styles.mainTableContainer}>
          <div className={styles.tableContainer}>
            <AttributesVirtualizedTable
              fixedColumns={columns}
              rowsData={{ ...asnData, data: filteredAsnData }}
              maxEmptyRowPadding={9}
              fixedColumnsStyles={{
                headerStyles: {
                  color: "#0275A7",
                  fontSize: "13px",
                  fontWeight: "600",
                  boxSizing: "border-box",
                },
              }}
              optionalColumnsStyles={{
                headerStyles: {
                  color: "#0275a7",
                  backgroundColor: "white",
                  zIndex: 1,
                  boxSizing: "border-box",
                },
              }}
            />
          </div>
        </div>

        <div className={styles.footer}>
          <Button
            className={`${styles.footerButton} ${styles.backButton}`} 
            variant="contained"
            onClick={handleClose} 
          >
            Back
          </Button>
          <Button
            className={styles.footerButton}
            variant="contained"
            onClick={(filteredData) => handleAsnsForLineTransferSelection(filteredData)}
            disabled={selectedRows.length === 0}
          >
            LINE TRANSFER
          </Button>
        </div>
      </>
    );
  };
  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="heijunka-sequence-modal"
        aria-describedby="modal to show heijunka sequence"
      >
        <div className={styles.container}>
          {asnData.loading ? <LinearProgress /> : renderTableView()}
        </div>
      </Modal>

      <LineTransferSelectorDisplay
        open={isAsnSelectorOpen}
        handleClose={closeAsnSelector}
        asnData={transferedAsnData}
        setAsnData={setTransferedAsnData}
        handleLineOnSubmit={openLineTransferModel}
        asnsSelectedForLineTransfer={asnToTransfer}
        lastAsnOrAgsnGeneratedDate={lastAsnGeneratedDate}
        activeDate={startDate}
        setActiveDate={setActiveDate}
        fixedColumns={fixedAsnSelectorColumns}
        asnSelectorheaderText={ASN_SELECTOR_HEADER_TEXT}
        selectedLine={lineName}
      />
      {isLineTransferASNDisplayOpen ? (
        <CommentFormModal
          open={isLineTransferASNDisplayOpen}
          handleClose={closeLineTransferModel}
          title="Line-Transfer"
          rowsData={transferedAsnData}
          lineBackgroundColor="#82C1A7"
          reasons={reasons}
          agencies={agencies}
          handleSubmit={(rows, reason, agency, comment) => {
            transferAsnsToLine(rows, reason, agency, comment);
          }}
        />
      ) : null}
    </>
  );
};

DayWiseFinishedGoodsLineTransferDisplay.propTypes = {
  enableLineSelection: PropTypes.bool,
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  updateRows: PropTypes.func.isRequired,
  startDate: PropTypes.objectOf(moment),
  endDate: PropTypes.objectOf(moment),
  plant: PropTypes.string.isRequired,
  line: PropTypes.string.isRequired,
  lineName: PropTypes.string.isRequired,
  toline: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
};

export default DayWiseFinishedGoodsLineTransferDisplay;
